/**
 * VisionFlexPlayer - Iframe-based VisionFlex video call integration
 * 
 * This component replaces the Amazon Chime SDK implementation with VisionFlex.
 * It uses an iframe to embed the VisionFlex video call interface.
 * 
 * Based on official VisionFlex API specification:
 * - Authentication via JWT tokens (handled by backend)
 * - Session joining via URL with id_token parameter
 * - Fully managed video interface within iframe
 * 
 * Author: AI Assistant
 * Date: 2025-10-14
 */

import React, { useState, useEffect, useRef } from 'react';
import { Loader, AlertTriangle, PhoneOff, RefreshCw, Maximize2, Square } from 'lucide-react';
import apiClient from '../../services/apiClient';

const VisionFlexPlayer = ({ 
  bookingId, 
  userRole, 
  appointmentData,
  onCallEnd,
  onVideoSizeChange, // NEW: Callback when video size changes
  videoSize: externalVideoSize, // NEW: Controlled video size from parent
  className = "",
  isEmbedded = false // New prop: true for telenotes pages, false for standalone page
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joinUrl, setJoinUrl] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [videoSize, setVideoSize] = useState(externalVideoSize || 'wide'); // Use external or default to 'wide' (16:9)
  const iframeRef = useRef(null);

  useEffect(() => {
    initializeVisionFlexCall();
    
    // Monitor iframe load status
    const handleIframeLoad = () => {
      console.log('✅ VisionFlex iframe loaded successfully');
      setIsConnected(true);
    };

    const iframe = iframeRef.current;
    if (iframe) {
      iframe.addEventListener('load', handleIframeLoad);
      return () => iframe.removeEventListener('load', handleIframeLoad);
    }
  }, [bookingId]);

  // Sync external videoSize changes with internal state
  useEffect(() => {
    if (externalVideoSize && externalVideoSize !== videoSize) {
      console.log('📐 External video size changed to:', externalVideoSize);
      setVideoSize(externalVideoSize);
    }
  }, [externalVideoSize]);

  const initializeVisionFlexCall = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log(`🎥 Initializing VisionFlex call for booking ${bookingId}`);
      console.log(`   User Role: ${userRole}`);
      console.log(`   Appointment Type: ${appointmentData?.appointment_type}`);

      // Join the video session (backend handles VisionFlex authentication and token generation)
      const response = await apiClient.post(`/video-calls/sessions/${bookingId}/join`, {
        device_info: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          screen: {
            width: window.screen.width,
            height: window.screen.height
          }
        },
        browser_info: navigator.userAgent,
        user_agent: navigator.userAgent
      });

      const data = response.data;
      console.log('✅ VisionFlex session joined successfully');
      console.log('Session Info:', {
        sessionId: data.session_info?.session_id,
        classId: data.meeting_config?.class_id,
        hasJoinUrl: !!data.participant_credentials?.join_url
      });

      // The join_url already contains the JWT token as query parameter
      const fullJoinUrl = data.participant_credentials?.join_url;
      
      if (!fullJoinUrl) {
        throw new Error('No join URL received from server');
      }

      console.log('🔗 VisionFlex Join URL:', fullJoinUrl.substring(0, 100) + '...');
      console.log('📋 Join URL Details:');
      console.log('   - Full URL length:', fullJoinUrl.length);
      console.log('   - Has JWT token:', fullJoinUrl.includes('id_token='));
      console.log('   - Class ID:', data.meeting_config?.class_id);

      setJoinUrl(fullJoinUrl);
      setSessionInfo(data.session_info);
      setIsLoading(false);
      setRetryCount(0);  // Reset retry count on success

    } catch (err) {
      console.error('❌ Error initializing VisionFlex:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to join video call';
      setError(errorMessage);
      setIsLoading(false);
      
      // Auto-retry logic (max 3 attempts)
      if (retryCount < 3 && err.response?.status >= 500) {
        console.log(`🔄 Auto-retrying... (attempt ${retryCount + 1}/3)`);
        setRetryCount(prev => prev + 1);
        setTimeout(() => {
          initializeVisionFlexCall();
        }, 2000 * (retryCount + 1));  // Exponential backoff
      }
    }
  };

  const handleEndCall = async () => {
    try {
      console.log('📞 Ending video call...');
      await apiClient.delete(`/video-calls/sessions/${bookingId}/leave`, {
        data: {
          reason: 'user_ended_call'
        }
      });
      console.log('✅ Left video session successfully');
      if (onCallEnd) onCallEnd();
    } catch (err) {
      console.error('❌ Error ending call:', err);
      // Still call onCallEnd to clean up UI even if API call fails
      if (onCallEnd) onCallEnd();
    }
  };

  const handleManualRetry = () => {
    setRetryCount(0);  // Reset retry count
    initializeVisionFlexCall();
  };

  // Handle video size change with smooth animation
  const handleSizeChange = (newSize) => {
    setVideoSize(newSize);
    // Notify parent component about size change
    if (onVideoSizeChange) {
      onVideoSizeChange(newSize);
    }
  };

  // Determine container class based on context and size
  const getContainerClass = () => {
    if (!isEmbedded) return 'h-screen w-full'; // Standalone page always full screen
    
    // Embedded mode with two size options: 16:9 wide frame and fullscreen
    switch(videoSize) {
      case 'wide':
        return 'h-[540px] w-[960px] min-w-[960px]'; // Wide 16:9 aspect ratio (960x540)
      case 'fullscreen':
        return 'h-screen w-screen fixed inset-0 z-[100]'; // Fullscreen overlay
      default:
        return 'h-[540px] w-[960px] min-w-[960px]'; // Default to wide 16:9
    }
  };
  
  const containerClass = getContainerClass();

  // Loading state
  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${containerClass} bg-gray-900 ${className}`}>
        <div className="text-center text-white max-w-md">
          <Loader className="w-16 h-16 animate-spin mx-auto mb-6 text-blue-500" />
          <h2 className="text-2xl font-bold mb-2">Connecting to VisionFlex...</h2>
          <p className="text-gray-400 mb-4">Please wait while we prepare your video call</p>
          {retryCount > 0 && (
            <p className="text-yellow-400 text-sm">
              Retry attempt {retryCount}/3...
            </p>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`flex items-center justify-center ${containerClass} bg-gray-900 ${className}`}>
        <div className="text-center text-white max-w-md px-4">
          <AlertTriangle className="w-20 h-20 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-bold mb-3">Connection Error</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          
          <div className="space-y-3">
            <button
              onClick={handleManualRetry}
              className="w-full bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              <span>Retry Connection</span>
            </button>
            
            <button
              onClick={handleEndCall}
              className="w-full bg-gray-700 hover:bg-gray-600 px-6 py-3 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
          
          {retryCount > 0 && (
            <p className="text-gray-500 text-sm mt-4">
              Previous attempts: {retryCount}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Main video call interface
  return (
    <div className={`relative ${containerClass} bg-black ${className} transition-all duration-500 ease-in-out ${videoSize === 'fullscreen' ? '' : 'rounded-xl border-4 border-blue-500 shadow-2xl'}`}>
      {/* VisionFlex Iframe - URL already contains JWT token */}
      <iframe
        ref={iframeRef}
        src={joinUrl}
        className={`w-full h-full border-0 ${videoSize === 'fullscreen' ? '' : 'rounded-lg'}`}
        allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-write"
        title="VisionFlex Video Call"
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads"
        style={{ border: 'none', pointerEvents: 'auto' }}
      />

      {/* Size Control Buttons (only in embedded mode) - Bottom-left corner with two options */}
      {isEmbedded && (
        <div className="absolute bottom-4 left-4 z-50 flex items-center space-x-2 bg-gray-900/90 backdrop-blur-sm rounded-lg p-1.5 shadow-lg transition-all duration-300" style={{ pointerEvents: 'auto' }}>
          <button
            onClick={() => handleSizeChange('wide')}
            className={`p-2 rounded transition-all duration-200 ${
              videoSize === 'wide' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            title="Wide 16:9 (960×540px)"
            style={{ pointerEvents: 'auto' }}
          >
            <Square className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleSizeChange('fullscreen')}
            className={`p-2 rounded transition-all duration-200 ${
              videoSize === 'fullscreen' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            title="Fullscreen"
            style={{ pointerEvents: 'auto' }}
          >
            <Maximize2 className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* End Call Button - Repositioned to bottom-right corner */}
      <div className="absolute bottom-4 right-4 z-50" style={{ pointerEvents: 'auto' }}>
        <button
          onClick={handleEndCall}
          className="bg-red-600 hover:bg-red-700 active:bg-red-800 text-white px-4 py-2 rounded-lg flex items-center space-x-2 shadow-xl transition-all hover:scale-105 active:scale-95 text-sm font-semibold"
          title="End video call"
          style={{ pointerEvents: 'auto' }}
        >
          <PhoneOff className="w-4 h-4" />
          <span>End Call</span>
        </button>
      </div>

      {/* Session Info (for debugging) - moved to top-left */}
      {sessionInfo && process.env.NODE_ENV === 'development' && (
        <div className="absolute top-4 left-4 bg-black bg-opacity-70 text-white px-3 py-2 rounded text-xs z-40">
          <div>Session: {sessionInfo.session_id?.substring(0, 8)}...</div>
          <div>Booking: {bookingId}</div>
          <div>Role: {userRole}</div>
        </div>
      )}
    </div>
  );
};

export default VisionFlexPlayer;

